/**
 * /tools/performance-review-generator — a free, client-side generator of
 * performance review and 360° feedback questions.
 *
 * A visitor picks a competency, a role family and a review type, and gets three
 * behaviour statements to rate, three open questions and three comment stems,
 * with a copy button and an optional request for a PDF copy by email. Everything
 * the page renders — the competency library, the question templates, the copy
 * and the FAQ — comes from this file, and so does the route's JSON-LD
 * (`getSchemaForReviewGenerator`), so the markup cannot describe a question or
 * an answer the visitor does not see. The route is wired like every other public
 * page: the path in `MarketingRoutes.tsx` and `MarketingRoutes.server.tsx`,
 * `REVIEW_GENERATOR_PATH` in `prerenderRoutes`, and SEO resolution in
 * `seo/siteSeo.ts`.
 *
 * WHERE THE CONTENT COMES FROM. The structure is the Competency Assessment
 * Framework that /solutions/competency-management-software describes: each
 * competency is written as observable behaviours rather than adjectives, the
 * behaviours differ for individual-contributor, technical and leadership roles
 * (the page's "role-specific scales"), and every rating is backed by evidence.
 * The wording is CLEAR Talent's own starter copy, not a validated instrument or
 * a customer's framework, and the page says so.
 *
 * There is no /tools index, so `/tools` itself stays a true 404. The breadcrumb
 * runs through the competency management page the tool supports, the way a
 * free resource's runs through its solution page, and that page links back.
 */
import { SCHEMA_CONTEXT, type JsonLd } from '../seo/structuredData';
import { breadcrumbListSchema, type BreadcrumbItem } from '../seo/breadcrumbs';
import { SOLUTION_PAGE_BY_SLUG } from './solution-pages';

// Restated rather than imported from `seo/siteSeo.ts`, which imports this file.
const SITE_ORIGIN = 'https://www.clear-talent.com';
const ORG_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
const SOFTWARE_ID = `${SITE_ORIGIN}/#software`;

export const REVIEW_GENERATOR_PATH = '/tools/performance-review-generator';

/** The GA4 `tool_name` parameter on the tool's `tool_generate` and `tool_copy` events. */
export const REVIEW_GENERATOR_TOOL_NAME = 'performance_review_generator';

/**
 * Published, and last changed. Move `REVIEW_GENERATOR_LAST_MODIFIED` forward
 * when the questions or the copy change, and not otherwise — it is the sitemap
 * `<lastmod>`, the WebPage `dateModified` and the visible "Last updated" line.
 */
export const REVIEW_GENERATOR_PUBLISHED = '2026-10-03';
export const REVIEW_GENERATOR_LAST_MODIFIED = '2026-10-03';
export const REVIEW_GENERATOR_LAST_MODIFIED_LABEL = '3 October 2026';

const COMPETENCY_PAGE = SOLUTION_PAGE_BY_SLUG['competency-management-software'];
export const COMPETENCY_PAGE_PATH = `/${COMPETENCY_PAGE.category}/${COMPETENCY_PAGE.slug}`;

export type RoleId = 'individual-contributor' | 'technical-specialist' | 'people-manager';
export type ReviewTypeId = 'self' | 'manager' | 'peer360';

export interface RoleOption {
  id: RoleId;
  label: string;
  description: string;
}

export interface ReviewTypeOption {
  id: ReviewTypeId;
  label: string;
  /** The instruction printed above the behaviour statements. */
  ratingInstruction: string;
}

export interface CompetencyEntry {
  id: string;
  name: string;
  /** Lower-case form used inside generated sentences ("your decision-making"). */
  noun: string;
  definition: string;
  /** Three observable behaviours per role family — the statements a rater scores. */
  behaviours: Record<RoleId, [string, string, string]>;
  /** What evidence of the competency typically looks like for the role. */
  evidence: Record<RoleId, string>;
}

/**
 * The role families the competency page's "role-specific scales" names:
 * individual-contributor, technical and leadership roles.
 */
export const REVIEW_ROLES: RoleOption[] = [
  {
    id: 'individual-contributor',
    label: 'Individual contributor',
    description: 'Delivers their own work and has no direct reports.',
  },
  {
    id: 'technical-specialist',
    label: 'Technical or specialist role',
    description: 'Engineers, clinicians, analysts and others judged on expert work.',
  },
  {
    id: 'people-manager',
    label: 'People manager',
    description: 'Leads a team and is accountable for its results and development.',
  },
];

export const REVIEW_TYPES: ReviewTypeOption[] = [
  {
    id: 'self',
    label: 'Self-assessment',
    ratingInstruction:
      'Rate how consistently you do each of these on your organisation’s scale, and note one example behind each rating.',
  },
  {
    id: 'manager',
    label: 'Manager review',
    ratingInstruction:
      'Rate how consistently they do each of these on your organisation’s scale, and back every rating with an example.',
  },
  {
    id: 'peer360',
    label: '360° / peer feedback',
    ratingInstruction:
      'Rate how consistently this person does each of these. Rate only what you have seen yourself, and leave the rest blank.',
  },
];

export const REVIEW_COMPETENCIES: CompetencyEntry[] = [
  {
    id: 'communication',
    name: 'Communication',
    noun: 'communication',
    definition: 'Sharing information clearly, listening well and adapting the message to the audience.',
    behaviours: {
      'individual-contributor': [
        'Writes updates a colleague can act on without a follow-up question.',
        'Listens to understand before responding, and checks what they heard.',
        'Raises concerns early and directly rather than letting them build.',
      ],
      'technical-specialist': [
        'Explains technical trade-offs in terms a non-specialist can act on.',
        'Documents decisions and handovers so others can pick up the work.',
        'Flags risks in their area before they affect other teams.',
      ],
      'people-manager': [
        'Explains the reasoning behind team priorities, not just the priorities.',
        'Gives feedback that is specific enough for the person to act on.',
        'Keeps stakeholders informed when plans or timelines change.',
      ],
    },
    evidence: {
      'individual-contributor': 'a written update, a handover or a meeting',
      'technical-specialist': 'a design note, an incident write-up or a handover',
      'people-manager': 'a team briefing, a one-to-one or a stakeholder update',
    },
  },
  {
    id: 'collaboration',
    name: 'Collaboration',
    noun: 'collaboration',
    definition: 'Working effectively with others towards shared goals, inside and outside the team.',
    behaviours: {
      'individual-contributor': [
        'Follows through on commitments made to colleagues.',
        'Offers help to colleagues without being asked.',
        'Is open to ideas that differ from their own.',
      ],
      'technical-specialist': [
        'Shares expertise so others can work without depending on them.',
        'Reviews colleagues’ work constructively and on time.',
        'Works effectively with teams outside their own discipline.',
      ],
      'people-manager': [
        'Builds working relationships with other teams that unblock their own.',
        'Shares credit for joint work with the people who did it.',
        'Resolves conflict in the team constructively rather than avoiding it.',
      ],
    },
    evidence: {
      'individual-contributor': 'a joint project or a handover',
      'technical-specialist': 'a review of a colleague’s work or a cross-team project',
      'people-manager': 'a cross-team project or a disagreement resolved',
    },
  },
  {
    id: 'decision-making',
    name: 'Decision-making',
    noun: 'decision-making',
    definition: 'Making sound, timely decisions and explaining the trade-offs behind them.',
    behaviours: {
      'individual-contributor': [
        'Makes everyday decisions within their remit without waiting to be told.',
        'Gathers the information a decision needs — no more, no less.',
        'Escalates the decisions that should be escalated, and takes the ones that should not.',
      ],
      'technical-specialist': [
        'Makes sound technical judgements when information is incomplete.',
        'Explains the trade-offs behind a recommendation.',
        'Changes course when new evidence shows a decision was wrong.',
      ],
      'people-manager': [
        'Makes decisions in good time, without waiting for certainty that will not come.',
        'Involves the people a decision affects before making it.',
        'Supports a decision once it is made, including ones they argued against.',
      ],
    },
    evidence: {
      'individual-contributor': 'an everyday call and how it turned out',
      'technical-specialist': 'a recommendation or a design choice',
      'people-manager': 'a prioritisation call or a change of plan',
    },
  },
  {
    id: 'problem-solving',
    name: 'Problem-solving',
    noun: 'problem-solving',
    definition: 'Finding the cause of a problem and putting a workable fix in place.',
    behaviours: {
      'individual-contributor': [
        'Looks for the cause of a problem rather than only the symptom.',
        'Brings a suggested solution when raising a problem.',
        'Checks that a fix has actually worked.',
      ],
      'technical-specialist': [
        'Breaks complex problems into parts that can be tested.',
        'Uses evidence, not assumption, to diagnose what went wrong.',
        'Prevents repeat problems by fixing the underlying process.',
      ],
      'people-manager': [
        'Helps the team find causes rather than assign blame.',
        'Removes obstacles the team cannot remove on its own.',
        'Turns recurring problems into changes to how the team works.',
      ],
    },
    evidence: {
      'individual-contributor': 'a problem spotted and fixed',
      'technical-specialist': 'a diagnosis or a root-cause review',
      'people-manager': 'an obstacle removed or a process changed',
    },
  },
  {
    id: 'accountability',
    name: 'Accountability',
    noun: 'accountability',
    definition: 'Owning commitments and outcomes, including when things go wrong.',
    behaviours: {
      'individual-contributor': [
        'Delivers agreed work to the standard and deadline agreed.',
        'Says early when a deadline is at risk.',
        'Takes responsibility for mistakes and puts them right.',
      ],
      'technical-specialist': [
        'Holds their own work to the professional standards of their field.',
        'Owns the quality of what they hand over, not just its completion.',
        'Follows up on issues in their area until they are resolved.',
      ],
      'people-manager': [
        'Holds the team to clear standards and holds themselves to the same.',
        'Takes responsibility for the team’s results, good and bad.',
        'Follows through on what they promised their team.',
      ],
    },
    evidence: {
      'individual-contributor': 'a deadline met or a risk flagged early',
      'technical-specialist': 'a quality check or an issue followed through',
      'people-manager': 'a team result or a commitment kept',
    },
  },
  {
    id: 'leadership',
    name: 'Leadership',
    noun: 'leadership',
    definition: 'Setting direction, developing people and influencing others — with or without a title.',
    behaviours: {
      'individual-contributor': [
        'Influences decisions through evidence and well-made arguments.',
        'Helps new colleagues get up to speed.',
        'Models the standards they expect of others.',
      ],
      'technical-specialist': [
        'Sets technical direction that others can follow.',
        'Mentors less experienced colleagues in their discipline.',
        'Represents their area’s view clearly to senior stakeholders.',
      ],
      'people-manager': [
        'Sets clear priorities so the team knows what matters most.',
        'Makes time to develop each person in their team.',
        'Delegates with enough context and authority for the work to be done well.',
      ],
    },
    evidence: {
      'individual-contributor': 'an idea championed or a colleague helped',
      'technical-specialist': 'a technical direction set or a colleague mentored',
      'people-manager': 'a priority set or a person developed',
    },
  },
];

export interface ReviewSelection {
  competencyId: string;
  roleId: RoleId;
  reviewTypeId: ReviewTypeId;
}

/** The set the page renders before anyone touches the form — and the one a crawler sees. */
export const DEFAULT_REVIEW_SELECTION: ReviewSelection = {
  competencyId: 'communication',
  roleId: 'individual-contributor',
  reviewTypeId: 'self',
};

export interface GeneratedReviewSet {
  competency: CompetencyEntry;
  role: RoleOption;
  reviewType: ReviewTypeOption;
  heading: string;
  behaviours: string[];
  questions: string[];
  stems: string[];
}

const openQuestions = (type: ReviewTypeId, noun: string, evidence: string): string[] => {
  switch (type) {
    case 'self':
      return [
        `Which piece of work this period best shows your ${noun}? Point to the evidence, such as ${evidence}.`,
        `Where did your ${noun} fall short of the standard you set yourself, and what would you do differently?`,
        `What one change to your ${noun} would make the biggest difference to your work next period?`,
      ];
    case 'manager':
      return [
        `What is the clearest example this period of their ${noun} making a difference? Point to the evidence, such as ${evidence}.`,
        `Where did their ${noun} hold back their own work or the team’s, and how often did it happen?`,
        `What one development action would most strengthen their ${noun} before the next review?`,
      ];
    case 'peer360':
      return [
        `When has this person’s ${noun} made your work easier? Describe one specific situation.`,
        `What, if anything, about this person’s ${noun} makes working with them harder?`,
        `What should this person do more of, or less of, to strengthen their ${noun}?`,
      ];
  }
};

const commentStems = (type: ReviewTypeId, noun: string): string[] => {
  switch (type) {
    case 'self':
      return [
        `My ${noun} was strongest this period when I …`,
        'The evidence for this is …',
        `To strengthen my ${noun} next period, I will …`,
      ];
    case 'manager':
      return [
        `[Name]’s ${noun} was strongest this period when they …`,
        'The clearest evidence of this is …',
        'To reach the next proficiency level, [Name] should focus on …',
      ];
    case 'peer360':
      return [
        `Something this person does well in ${noun} is …`,
        'One situation where I saw this was …',
        'It would help me if they …',
      ];
  }
};

const byId = <T extends { id: string }>(list: T[], id: string): T => list.find((item) => item.id === id) ?? list[0];

/** The question set for one selection. Pure, so the prerender and the browser agree. */
export const generateReviewSet = (selection: ReviewSelection): GeneratedReviewSet => {
  const competency = byId(REVIEW_COMPETENCIES, selection.competencyId);
  const role = byId(REVIEW_ROLES, selection.roleId);
  const reviewType = byId(REVIEW_TYPES, selection.reviewTypeId);

  return {
    competency,
    role,
    reviewType,
    heading: `${competency.name} · ${role.label} · ${reviewType.label}`,
    behaviours: [...competency.behaviours[role.id]],
    questions: openQuestions(reviewType.id, competency.noun, competency.evidence[role.id]),
    stems: commentStems(reviewType.id, competency.noun),
  };
};

/** The plain text the copy button puts on the clipboard. */
export const reviewSetToText = (set: GeneratedReviewSet): string =>
  [
    set.heading,
    set.competency.definition,
    '',
    'Behaviours to rate',
    set.reviewType.ratingInstruction,
    ...set.behaviours.map((line, index) => `${index + 1}. ${line}`),
    '',
    'Open questions',
    ...set.questions.map((line, index) => `${index + 1}. ${line}`),
    '',
    'Comment stems',
    ...set.stems.map((line) => `- ${line}`),
    '',
    `Generated with the free CLEAR Talent review question generator: ${SITE_ORIGIN}${REVIEW_GENERATOR_PATH}`,
  ].join('\n');

/** The pre-filled request for a PDF copy, through the contact form every gated sample uses. */
export const reviewSetPdfRequestHref = (set: GeneratedReviewSet): string => {
  const params = new URLSearchParams({
    reason: 'review-questions-pdf',
    source: 'review-question-generator',
    detail: `Set: ${set.competency.name}, ${set.role.label}, ${set.reviewType.label}.`,
  });
  return `/contact?${params.toString()}`;
};

export interface ReviewGeneratorFaq {
  q: string;
  a: string;
}

export interface ReviewGeneratorPage {
  path: string;
  seoTitle: string;
  metaDescription: string;
  name: string;
  eyebrow: string;
  h1: string;
  excerpt: string;
  howToUse: Array<{ title: string; body: string }>;
  faqs: ReviewGeneratorFaq[];
  internalLinks: Array<{ label: string; href: string }>;
}

export const REVIEW_GENERATOR_PAGE: ReviewGeneratorPage = {
  path: REVIEW_GENERATOR_PATH,
  seoTitle: 'Performance Review Question Generator | CLEAR Talent',
  metaDescription:
    'Free performance review and 360 feedback question generator. Pick a role, competency and review type for rating items, open questions and comment stems.',
  name: 'Performance review and 360° question generator',
  eyebrow: 'Free tool',
  h1: 'Performance review and 360° feedback question generator',
  excerpt:
    'Pick a competency, the kind of role and the type of review. You get three behaviours to rate, three open questions and three comment stems, written for that combination — ready to copy into your own review form. No sign-up.',
  howToUse: [
    {
      title: 'Choose what the review has to assess',
      body: 'Pick one competency per set. A review that asks about six competencies in one sitting gets six shallow answers; two or three, asked well, produce evidence a manager can use.',
    },
    {
      title: 'Match the role and the review type',
      body: 'The behaviours change with the role, because good communication from an engineer does not look like good communication from a team lead. The questions and stems change with the review type, because a self-assessment, a manager’s judgement and a peer’s observation answer different questions.',
    },
    {
      title: 'Ask for an example behind every rating',
      body: 'A rating with no example is an opinion. Ask raters to name the piece of work behind each score, so the review conversation is about what happened rather than about the number.',
    },
    {
      title: 'Finish the comment stems, then edit them',
      body: 'The stems are there to get written feedback started. Replace each ellipsis with a specific observation, and delete any stem that does not fit what you actually saw.',
    },
  ],
  faqs: [
    {
      q: 'Is the review question generator free?',
      a: 'Yes. It runs in your browser with no sign-up, and you can copy any question set straight into your own review form, survey tool or spreadsheet. Asking for a PDF copy by email is optional.',
    },
    {
      q: 'Where do the questions come from?',
      a: 'They follow the structure of CLEAR Talent’s Competency Assessment Framework: each competency is described as observable behaviours rather than adjectives, the behaviours differ for individual contributors, technical specialists and people managers, and every rating is meant to be backed by evidence. The wording is starter copy written by the CLEAR Talent team, so adapt it to your own competency framework.',
    },
    {
      q: 'Which rating scale should I use with the behaviour statements?',
      a: 'The scale your organisation already uses. Each statement works with a frequency scale, from “rarely” to “consistently”, or with a proficiency scale. In CLEAR Talent, rating scales are configurable from three to seven levels.',
    },
    {
      q: 'Can I use the same behaviours for self-assessment, manager review and 360 feedback?',
      a: 'Yes, and it is deliberate that the behaviour statements stay the same across all three. When the employee, the manager and peers rate the same behaviours, the gaps between their views are what the review conversation should be about. The open questions and comment stems change with the review type.',
    },
    {
      q: 'How does this work inside CLEAR Talent?',
      a: 'In CLEAR Talent you define each competency once in a central library, with behavioural anchors and proficiency levels, and it flows into reviews, 360° feedback and development plans. Competency gaps from an assessment feed directly into Individual Development Plans.',
    },
  ],
  internalLinks: [
    { label: 'Competency management software', href: COMPETENCY_PAGE_PATH },
    { label: '360° feedback software', href: '/solutions/360-degree-feedback-software' },
    { label: 'Performance review software', href: '/solutions/employee-performance-review-software' },
    { label: 'Guide: 360 feedback questions by competency', href: '/blog/360-feedback-questions' },
    { label: 'Glossary: what is a competency framework?', href: '/glossary/competency-framework' },
  ],
};

/** Home > Solutions > Competency Management Software > the tool. */
export const getReviewGeneratorBreadcrumbs = (page: ReviewGeneratorPage = REVIEW_GENERATOR_PAGE): BreadcrumbItem[] => [
  { name: 'Home', path: '/' },
  { name: 'Solutions', path: '/solutions' },
  { name: COMPETENCY_PAGE.primaryKeyword, path: COMPETENCY_PAGE_PATH },
  { name: 'Review question generator', path: page.path },
];

/**
 * The route's nodes: WebPage, the WebApplication it hosts, FAQPage and
 * BreadcrumbList. `siteSeo` adds the Organization, WebSite and product nodes the
 * references below resolve to. The WebApplication describes this free tool —
 * a zero-price Offer, no rating — not the CLEAR Talent product, which keeps its
 * own SoftwareApplication node.
 */
export const getSchemaForReviewGenerator = (page: ReviewGeneratorPage = REVIEW_GENERATOR_PAGE): JsonLd[] => {
  const url = `${SITE_ORIGIN}${page.path}`;
  const toolId = `${url}#tool`;

  return [
    {
      '@context': SCHEMA_CONTEXT,
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: page.seoTitle,
      description: page.metaDescription,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': SOFTWARE_ID },
      mainEntity: { '@id': toolId },
      publisher: { '@id': ORG_ID },
      datePublished: REVIEW_GENERATOR_PUBLISHED,
      dateModified: REVIEW_GENERATOR_LAST_MODIFIED,
      inLanguage: 'en',
    },
    {
      '@context': SCHEMA_CONTEXT,
      '@type': 'WebApplication',
      '@id': toolId,
      name: page.name,
      url,
      description: page.metaDescription,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      browserRequirements: 'Requires JavaScript.',
      isAccessibleForFree: true,
      inLanguage: 'en',
      provider: { '@id': ORG_ID },
      offers: {
        '@type': 'Offer',
        price: 0,
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
      },
    },
    {
      '@context': SCHEMA_CONTEXT,
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: page.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    },
    breadcrumbListSchema(getReviewGeneratorBreadcrumbs(page)),
  ];
};
